import React, { useEffect, useMemo, useState } from 'react'
import * as antd from 'antd'
import styled from 'styled-components'
import { usePaySSAMMerchant } from 'app/gomsacademy/hooks/PaySSAMMerchant'
import { LectureWrapper, LectureView, LectureViewHeader } from '../Lecture/styles'

const REVIEW_STATUS_META = {
	O: { label: '개시 완료', color: 'success' },
	P: { label: '심사 중', color: 'processing' },
	W: { label: '작성 중', color: 'default' },
	S: { label: '보완 필요', color: 'warning' },
	R: { label: '반려', color: 'error' },
	N: { label: '심사 없음', color: 'default' },
}

const renderReviewStatus = (value?: string) => {
	const meta = REVIEW_STATUS_META[value as keyof typeof REVIEW_STATUS_META]
	if (!meta) return <antd.Tag>미확인</antd.Tag>
	return <antd.Tag color={meta.color}>{meta.label}</antd.Tag>
}

const SubMerchantList = () => {
	const { subMerchants, loadingSubMerchants, getSubMerchants, checkMerchantStatus } = usePaySSAMMerchant()
	const [search, setSearch] = useState('')
	const [checkingDeptId, setCheckingDeptId] = useState<number | null>(null)

	useEffect(() => {
		getSubMerchants().catch(() => {
			// error handled in hook
		})
	}, [getSubMerchants])

	const filteredData = useMemo(() => {
		const keyword = search.trim().toLowerCase()
		if (!keyword) return subMerchants
		return subMerchants.filter(item => {
			const values = [
				item.companyName,
				item.branchName,
				item.ceoName,
				item.email,
				item.memberId,
				item.merchantId,
				item.businessNumber,
			]
				.filter(Boolean)
				.map(value => String(value).toLowerCase())
			return values.some(value => value.includes(keyword))
		})
	}, [search, subMerchants])

	const handleCheckStatus = async (deptId?: number) => {
		if (!deptId) {
			antd.message.warning('지점 정보를 찾을 수 없습니다.')
			return
		}

		setCheckingDeptId(deptId)
		try {
			await checkMerchantStatus(deptId)
		} catch (err) {
			// error handled in hook
		} finally {
			setCheckingDeptId(null)
		}
	}

	return (
		<Wrapper>
			<LectureView>
				<LectureViewHeader>
					<p>하위사업장 조회</p>
					<HeaderActions>
						<antd.Input.Search
							allowClear
							placeholder="사업장명, 대표자, 이메일, member/merchant 검색"
							value={search}
							onChange={e => setSearch(e.target.value)}
							style={{ width: 320 }}
						/>
						<antd.Button onClick={() => getSubMerchants()} loading={loadingSubMerchants}>
							새로고침
						</antd.Button>
					</HeaderActions>
				</LectureViewHeader>

				<antd.Card style={{ marginBottom: 24 }}>
					<SummaryText>
						총 <strong>{filteredData.length}</strong>개 사업장
						{search.trim() ? ` / 전체 ${subMerchants.length}개 중 검색 결과` : ''}
					</SummaryText>
				</antd.Card>

				<antd.Card>
					<antd.Table
						rowKey={record => `${record.deptId || 'dept'}-${record.memberId || 'member'}-${record.merchantId || 'merchant'}`}
						loading={loadingSubMerchants}
						dataSource={filteredData}
						pagination={{ pageSize: 10, showSizeChanger: true }}
						scroll={{ x: 1300 }}
						locale={{ emptyText: '조회된 하위사업장이 없습니다.' }}
						columns={[
							{ title: '사업장명', dataIndex: 'companyName', key: 'companyName', width: 220, render: value => value || '-' },
							{ title: '지점명', dataIndex: 'branchName', key: 'branchName', width: 180, render: value => value || '-' },
							{ title: '대표자', dataIndex: 'ceoName', key: 'ceoName', width: 120, render: value => value || '-' },
							{ title: '이메일', dataIndex: 'email', key: 'email', width: 220, render: value => value || '-' },
							{ title: '사업자번호', dataIndex: 'businessNumber', key: 'businessNumber', width: 140, render: value => value || '-' },
							{ title: '연동상태', dataIndex: 'reviewStatus', key: 'reviewStatus', width: 120, render: renderReviewStatus },
							{ title: 'Member ID', dataIndex: 'memberId', key: 'memberId', width: 180, render: value => value || '-' },
							{ title: 'Merchant ID', dataIndex: 'merchantId', key: 'merchantId', width: 180, render: value => value || '-' },
							{
								title: '확인',
								key: 'action',
								width: 150,
								render: (_, record) => {
									const disabled = !record.deptId || !record.memberId || !record.merchantId
									return (
										<antd.Tooltip title={disabled ? 'Member ID와 Merchant ID가 있어야 PaySSAM 연동상태를 확인할 수 있습니다.' : ''}>
											<antd.Button
												size="small"
												disabled={disabled}
												loading={checkingDeptId === record.deptId}
												onClick={() => handleCheckStatus(record.deptId)}>
												연동상태 확인
											</antd.Button>
										</antd.Tooltip>
									)
								},
							},
						]}
					/>
				</antd.Card>
			</LectureView>
		</Wrapper>
	)
}

const Wrapper = styled(LectureWrapper)``

const HeaderActions = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	flex-wrap: wrap;
`

const SummaryText = styled.p`
	margin: 0;
	color: #666;
`

export default SubMerchantList